import * as z from 'zod';
import { merchantModerationStateSchema, optional, timestampSchema } from './common';
import { localizedTextSchema, pendingChangeRequestSchema } from './product';

// Product Category Service — `v1ProductCategory`, the menu taxonomy.
//
// Scope note (verified against `test.api.noor.uz`): categories are
// MERCHANT-wide, not per-restaurant. The spec declares a `restaurant_id` on
// `v1ProductCategory` and accepts one as a list filter, but the wire object
// carries `merchant_id` and no `restaurant_id`, and filtering by two different
// restaurants of the same merchant returns an identical set. The field is
// therefore modelled as optional rather than required — see `api/category-api`.

export const productCategorySchema = z.object({
  id: z.string(),
  name: z.string().default(''),
  is_active: z.boolean().default(true),
  icon: z.string().default(''),
  /** Position in the category list (1-based). */
  sort_order: z.number().int().min(1).optional(),
  created_at: optional(timestampSchema),
  updated_at: optional(timestampSchema),
  external_id: z.string().default(''),
  /** Fields overridden away from the imported menu source. */
  overridden_fields: z.array(z.string()).default([]),
  /** The owning merchant. Present on the wire; `restaurant_id` is not. */
  merchant_id: z.string().optional(),
  /** Declared by the spec but absent from every observed payload. */
  restaurant_id: z.string().optional(),
  /** Merchant-facing moderation state for category review workflows. */
  moderation_state: merchantModerationStateSchema.default(
    'MERCHANT_MODERATION_STATE_UNSPECIFIED',
  ),
  /** The moderator's note when `moderation_state` is REJECTED. */
  rejection_reason: z.string().optional(),
});
export type ProductCategory = z.infer<typeof productCategorySchema>;

/**
 * `v1LocalizedProductCategory` — the same field set as `ProductCategory`, but
 * with `name` exposed as EVERY language at once instead of one locale-resolved
 * string. Exactly the `Product` / `LocalizedProduct` split (see `product.ts`):
 * the merchant WRITE RPCs (create/update/reset-overrides) answer with this
 * shape because an editor round-trips all three translations, while the READ
 * RPCs (list/get) answer with the locale-resolved `ProductCategory`.
 *
 * Parsing a create response with the plain schema throws — `name` is an object,
 * not a string — which is what made a successful create surface as a failure.
 */
export const localizedProductCategorySchema = productCategorySchema.extend({
  name: localizedTextSchema.default({ ru: '', uz: '', en: '' }),
});
export type LocalizedProductCategory = z.infer<typeof localizedProductCategorySchema>;

/** Single-resource payload — `data` on get (locale-resolved `name`). */
export const productCategoryResponseSchema = z.object({
  category: productCategorySchema,
});

/**
 * Single-resource payload for the localized merchant READ —
 * `GET /product-categories/{id}/by-merchant` (`v1GetMerchantProductCategoryResponse`),
 * which answers with every translation at once. `category` is REQUIRED: a read
 * is never gated through moderation, so an absent category is contract drift.
 */
export const localizedProductCategoryResponseSchema = z.object({
  category: localizedProductCategorySchema,
});

/**
 * Single-resource payload for published category read (`GET /product-categories/{id}/published`).
 * Returned as `{ client_visible: true, category }` or `{ client_visible: false, category: null }`.
 */
export const publishedCategoryCardResponseSchema = z.object({
  client_visible: z.boolean().default(true),
  category: localizedProductCategorySchema.nullable().optional(),
});
export type PublishedCategoryCardResponse = z.infer<
  typeof publishedCategoryCardResponseSchema
>;

/**
 * `data` on `PATCH /product-categories/{id}` (`v1UpdateProductCategoryResponse`).
 *
 * `category` is OPTIONAL: when the merchant's writes are gated behind
 * moderation the update answers with only a `pending_change` receipt
 * (verified against test.api.noor.uz — the gated body is
 * `{result:'pending_change', pending_change:{…}}` with no category at all).
 * Requiring `category` made that SUCCESS parse as a failure, which is how a
 * queued rename surfaced as an error toast.
 */
export const categoryWriteResponseSchema = z.object({
  category: localizedProductCategorySchema.optional(),
  pending_change: pendingChangeRequestSchema.optional(),
});
export type CategoryWriteResult = z.infer<typeof categoryWriteResponseSchema>;

/** Receipt from the merchant category ordering endpoint. */
export const reorderProductCategoriesResponseSchema = z.object({
  reordered_category_ids: z.array(z.string()),
  skipped_category_ids: z.array(z.string()),
});
export type ReorderProductCategoriesResult = z.infer<
  typeof reorderProductCategoriesResponseSchema
>;
